import React, { useRef, useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import WritingLoader from './WritingLoader';
import ResponseLogger from './ResponseLogger'; // <-- Import new component
import { useNavigate } from 'react-router-dom';
import "./ProposalPreparation.css"

import {
  createProposal,
  editProposal,
  enhanceProposal,
  evaluateProposal,
  createWorkPlan,
  searchPublications,
  getPriorityText
} from "./api";

// const MIN_DURATION = 10000; // minimum duration per section (ms)
const RETRY_DELAY = 2000; // delay for retrying request (ms)

const ProposalPreparationPage = () => {
  const location = useLocation();
  const { idea_idx, title, min_duration, creative } = location.state || {};
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [delayAfterLog, setDelayAfterLog] = useState(0);
  const [responseLog, setResponseLog] = useState(null);
  const [error, setError] = useState(null);
  const isCancelledRef = useRef(false);

  const first_log = JSON.stringify(`Proje fikri, çok yönlü bir değerlendirme sürecine tabi tutulmuş olup, tüm temel bileşenleri titizlikle analiz edilmektedir. Öncelikle projenin genel amacı, kapsamı ve hedef kitlesi ayrıntılı olarak incelenmiştir. Kullanıcı ihtiyaçları, pazar dinamikleri ve teknolojik gereksinimler ışığında, fikir yapısının uygulanabilirliği değerlendirilmektedir. Bu kapsamda, proje fikrinin güçlü ve zayıf yönleri sistematik biçimde ortaya konmuştur.

Analiz süreci, hem niteliksel hem de niceliksel yöntemler kullanılarak yürütülmüş; mevcut kaynaklar, potansiyel riskler ve olası başarı faktörleri detaylı biçimde ele alınmıştır. Paydaş beklentileri, sektörel trendler ve benzer projelerle kıyaslamalar yapılarak, projenin benzersiz yönleri tespit edilmiştir. Teknik yeterliliklerin yanı sıra operasyonel sürdürülebilirlik ve ekonomik fizibilite de değerlendirme sürecine dahil edilmiştir.

Eksiklikler ve geliştirmeye açık alanlar, veri odaklı yaklaşımla belirlenmiş ve önceliklendirilmiştir. Bu doğrultuda, proje fikrinin optimize edilmesine yönelik öneriler geliştirilmiştir. Belirlenen eksik noktaların giderilmesi amacıyla uygulanabilir stratejiler tanımlanmış, yol haritası taslağı oluşturulmuştur. Gerekli görülen revizyonlar için uzman görüşleri alınmış ve çok disiplinli bir bakış açısı benimsenmiştir.

Tüm bu analizler sonucunda, proje fikrinin daha sağlam, etkili ve verimli bir yapıya kavuşturulması hedeflenmiştir. Nihai amaç, fikrin en yüksek katma değeri sağlayacak şekilde olgunlaştırılması ve hayata geçirilmesidir. Süreç boyunca elde edilen bulgular, karar alma mekanizmalarına şeffaf biçimde aktarılmak üzere dokümante edilmiştir.`);

  const logResponse = (step, message, delay) => {
    setResponseLog({ step, message });
    setDelayAfterLog(delay);
  };

  useEffect(() => {
    isCancelledRef.current = false;

    const runSteps = async () => {
      try {
        setCurrentStep(1);
        logResponse(1, first_log, 0);
        await wait(3000); // Step 0 delay

        if (isCancelledRef.current) return;
        setCurrentStep(2);
        const init_proposal = await runWithRetry(() => createProposal(idea_idx, creative), min_duration);
        logResponse(2, JSON.stringify(init_proposal), 250);

        if (isCancelledRef.current) return;
        setCurrentStep(3);
        const review = await runWithRetry(() => evaluateProposal(idea_idx), min_duration);
        logResponse(3, JSON.stringify(review), 250);

        if (isCancelledRef.current) return;
        setCurrentStep(4);
        const enhanced_proposal = await runWithRetry(() => enhanceProposal(idea_idx, review, creative), min_duration);
        logResponse(4, JSON.stringify(enhanced_proposal), 250);

        if (isCancelledRef.current) return;
        setCurrentStep(5);
        const final_proposal = await runWithRetry(() => editProposal(idea_idx), min_duration);
        logResponse(5, JSON.stringify(final_proposal), 250);

        if (isCancelledRef.current) return;
        setCurrentStep(6);
        const res = await runWithRetry(() => createWorkPlan(idea_idx), min_duration);
        const workplan = res.wp_list;
        const project_duration = res.project_duration;
        logResponse(6, JSON.stringify(workplan), 250);

        if (isCancelledRef.current) return;
        setCurrentStep(7);
        const priority = await runWithRetry(() => getPriorityText(idea_idx), min_duration);
        logResponse(7, JSON.stringify(priority), 300);
        
        setCurrentStep(8);
        await wait(1000);
        logResponse(8, JSON.stringify('Yayınlar taranıyor...'), min_duration);

        if (isCancelledRef.current) return;
        setCurrentStep(9);
        const publications = await runWithRetry(() => searchPublications(idea_idx), min_duration);
        logResponse(9, JSON.stringify(publications), 1000);

        setCurrentStep(10);
        await wait(10000);
        setCurrentStep(11);

        navigate('/project_writing_inputs', { state: { idea_idx: idea_idx, title: title,  method: final_proposal, priority: priority, wpList: workplan, publicationList: publications, projectDuration: project_duration} }); 

      } catch (err) {
        if (!isCancelledRef.current) {
          console.error("Step error:", err);
          setError('Beklenmeyen bir hata ile karşılaşıldı. Lütfen daha sonra tekrar deneyin.');
        }
      }
    };

    runSteps();

    return () => {
      isCancelledRef.current = true;
    };
  }, [idea_idx]);

  return (

    <div className="app-container">
      {/* Logo and Title Banners */}
      <img src="/logo_light_unified_noBG.png" alt="Logo" className="top-logo" />

      {error ? (
        <div style={{ color: 'red', padding: 20 }}>
          <h2>Hata</h2>
          <p>{error}</p>
        </div>
      ) : (
        <>
          <WritingLoader currentStep={currentStep} />
          <ResponseLogger log={responseLog} delayAfterLog={delayAfterLog} />
          </>
      )}
    </div>
  );
};

const wait = (ms) => new Promise(res => setTimeout(res, ms));

const runWithRetry = async (fn, min_duration, retryDelay = RETRY_DELAY) => {
  const start = Date.now();
  try {
    const res = await fn();
    const elapsed = Date.now() - start;
    if (elapsed < min_duration) await wait(min_duration - elapsed);
    return res;
  } catch (err) {
    console.warn("Retrying after failure...");
    await wait(retryDelay);
    const retryStart = Date.now();
    const res = await fn();
    const retryElapsed = Date.now() - retryStart;
    if (retryElapsed < min_duration) await wait(min_duration - retryElapsed);
    return res;
  }
};

const runWithoutRetry = async (fn, min_duration) => {
  const start = Date.now();
  const res = await fn();
  const elapsed = Date.now() - start;

  if (elapsed < min_duration) {
    await wait(min_duration - elapsed); 
  }

  return res;
};

export default ProposalPreparationPage;
